import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { EMPTY_TOUR_FILTERS, hasActiveTourFilters } from './filters';
import messages from './messages';

// Search + Status + City above the Setup → Tours table. Styled to match the
// Guest Reviews filter bar so the two admin lists read the same.
const selectClass =
  'text-sm text-slate-700 bg-white border border-solid border-slate-200 rounded-xl px-3 py-2 cursor-pointer focus:outline-none focus:border-indigo-500';

const ToursFilterBar = ({ filters, setFilters, cityOptions, shown, total }) => {
  const intl = useIntl();
  const patch = change => setFilters({ ...filters, ...change });

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="tours-filter-bar">
      <input
        type="search"
        value={filters.search}
        onChange={e => patch({ search: e.target.value })}
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        aria-label={intl.formatMessage(messages.searchLabel)}
        className="flex-1 min-w-[220px] max-w-sm text-sm text-slate-900 placeholder-slate-500 border border-solid border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-500"
      />

      <select
        value={filters.status}
        onChange={e => patch({ status: e.target.value })}
        aria-label={intl.formatMessage(messages.statusFilterLabel)}
        className={selectClass}>
        <option value="">{intl.formatMessage(messages.allStatuses)}</option>
        <option value="active">{intl.formatMessage(messages.statusActive)}</option>
        <option value="incomplete">{intl.formatMessage(messages.statusIncomplete)}</option>
      </select>

      {/* Only offered when there is more than one city to choose between. */}
      {cityOptions.length > 1 && (
        <select
          value={filters.city}
          onChange={e => patch({ city: e.target.value })}
          aria-label={intl.formatMessage(messages.cityFilterLabel)}
          className={selectClass}>
          <option value="">{intl.formatMessage(messages.allCities)}</option>
          {cityOptions.map(city => (
            <option key={city.value} value={city.value}>
              {city.label}
            </option>
          ))}
        </select>
      )}

      <span className="text-xs text-slate-500 font-semibold ml-auto" aria-live="polite">
        {shown === total
          ? intl.formatMessage(messages.countAll, { total })
          : intl.formatMessage(messages.countFiltered, { shown, total })}
      </span>

      {hasActiveTourFilters(filters) && (
        <button
          type="button"
          onClick={() => setFilters(EMPTY_TOUR_FILTERS)}
          className="text-sm font-bold text-indigo-700 hover:text-indigo-800 bg-transparent border-0 cursor-pointer px-2 py-2">
          {intl.formatMessage(messages.clearFilters)}
        </button>
      )}
    </div>
  );
};

ToursFilterBar.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    status: PropTypes.string,
    city: PropTypes.string
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  cityOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  shown: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired
};

export default ToursFilterBar;
